import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import FormSection from '../components/FormSection';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Venue } from '../api/types';

export default function VenuesPage() {
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const [editing, setEditing] = useState<Venue | null>(null);
  const [open, setOpen] = useState(false);

  const venuesQuery = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: async () => (await api.get('/venues')).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<Venue> & { id?: number }) => {
      if (payload.id) return (await api.patch(`/venues/${payload.id}`, payload)).data;
      return (await api.post('/venues', payload)).data;
    },
    onSuccess: () => {
      toast({ message: 'Venue saved', severity: 'success' });
      setOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/venues/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'Venue deleted', severity: 'info' });
      qc.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title="Venues"
        subtitle="Track tracks, fields, and stadiums you compete at."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
            aria-label="Add venue"
          >
            Add venue
          </Button>
        }
      />

      <Card>
        {venuesQuery.isLoading ? (
          <LoadingFallback />
        ) : (venuesQuery.data ?? []).length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title="No venues yet"
              description="Add your first venue so you can attach it to a meet."
              action={
                <Button variant="contained" onClick={() => setOpen(true)} aria-label="Add first venue">
                  Add venue
                </Button>
              }
            />
          </Box>
        ) : (
          <Table size="small" aria-label="Venues">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(venuesQuery.data ?? []).map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{v.name}</Typography>
                    {v.notes && (
                      <Typography variant="caption" color="text.secondary">{v.notes}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{[v.city, v.region].filter(Boolean).join(', ') || '—'}</TableCell>
                  <TableCell>{v.address || '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton
                        aria-label={`Edit ${v.name}`}
                        onClick={() => {
                          setEditing(v);
                          setOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label={`Delete ${v.name}`}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete venue?',
                            message: `Permanently delete ${v.name}?`,
                            destructive: true,
                          });
                          if (ok) deleteMutation.mutate(v.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <VenueDialog
        open={open}
        venue={editing}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        onSave={(payload) => saveMutation.mutate({ ...payload, id: editing?.id })}
        busy={saveMutation.isPending}
      />
    </Box>
  );
}

function VenueDialog({
  open,
  venue,
  onClose,
  onSave,
  busy,
}: {
  open: boolean;
  venue: Venue | null;
  onClose: () => void;
  onSave: (payload: Partial<Venue>) => void;
  busy: boolean;
}) {
  const [name, setName] = useState(venue?.name || '');
  const [address, setAddress] = useState(venue?.address || '');
  const [city, setCity] = useState(venue?.city || '');
  const [region, setRegion] = useState(venue?.region || '');
  const [postal, setPostal] = useState(venue?.postal_code || '');
  const [notes, setNotes] = useState(venue?.notes || '');

  useEffect(() => {
    if (open) {
      setName(venue?.name || '');
      setAddress(venue?.address || '');
      setCity(venue?.city || '');
      setRegion(venue?.region || '');
      setPostal(venue?.postal_code || '');
      setNotes(venue?.notes || '');
    }
  }, [open, venue]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{venue ? 'Edit venue' : 'Add venue'}</DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ name, address, city, region, postal_code: postal, notes });
        }}
        aria-label="Venue form"
      >
        <DialogContent>
          <FormSection title="Location">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Street address" value={address} onChange={(e) => setAddress(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="State / Region" value={region} onChange={(e) => setRegion(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="ZIP" value={postal} onChange={(e) => setPostal(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Grid>
            </Grid>
          </FormSection>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Save venue">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
